import TuneIcon from '@mui/icons-material/Tune'
import { Box, Button, Card, Divider, Menu, MenuItem, TextField, Typography, useTheme } from '@mui/material'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MdSearch } from 'react-icons/md'
import { useGetProductCategoriesForDropDown } from 'src/api/product-category'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { PositionDrawingFilterChipList } from './positionDrawingFilterChipList'
import {
  getPositionDrawingCompactSearchInputSx,
  getPositionDrawingFilterButtonSx,
  getPositionDrawingFilterDividerSx,
  getPositionDrawingFilterSurfaceSx,
  getPositionDrawingSearchBarSx,
  getPositionDrawingSearchInputSx,
  PositionDrawingFilterMenuItem,
} from './positionDrawingFilterStyles'
import { getProductCategoryDisplayName } from './positionDrawingFilterUtils'

export interface PositionDrawingFilterState {
  searchInput: string
  productCategoryIds: EntityId[]
}

interface PositionDrawingFiltersProps {
  filters: PositionDrawingFilterState
  onSearchChange: (value: string) => void
  onProductCategoriesChange: (categoryIds: EntityId[]) => void
  padding?: number
  /** Narrow layouts (e.g. select/update drawing modal) — full-width search row */
  compact?: boolean
}

export const PositionDrawingFilters: React.FC<PositionDrawingFiltersProps> = ({
  filters,
  onSearchChange,
  onProductCategoriesChange,
  padding = 2,
  compact = false,
}) => {
  const { t } = useTranslation(['admin-position-drawing', 'common'])
  const theme = useTheme()
  const [filterAnchor, setFilterAnchor] = useState<null | HTMLElement>(null)
  const { data: productCategories = [] } = useGetProductCategoriesForDropDown()

  const categoryById = useMemo(
    () => new Map(productCategories.map(category => [String(category.id), category])),
    [productCategories],
  )

  const sortedProductCategories = useMemo(
    () =>
      [...productCategories].sort((a, b) =>
        getProductCategoryDisplayName(a).localeCompare(getProductCategoryDisplayName(b)),
      ),
    [productCategories],
  )

  const addCategory = useCallback(
    (categoryId: EntityId) => {
      onProductCategoriesChange([...filters.productCategoryIds, categoryId])
    },
    [filters.productCategoryIds, onProductCategoriesChange],
  )

  const removeCategoryAt = useCallback(
    (index: number) => {
      onProductCategoriesChange(filters.productCategoryIds.filter((_, i) => i !== index))
    },
    [filters.productCategoryIds, onProductCategoriesChange],
  )

  const selectionCount = useCallback(
    (categoryId: EntityId) => filters.productCategoryIds.filter(id => id === categoryId).length,
    [filters.productCategoryIds],
  )

  return (
    <Card sx={getPositionDrawingFilterSurfaceSx(theme, padding)} elevation={0} variant="outlined">
      <Box sx={getPositionDrawingSearchBarSx(theme, compact)}>
        <Box
          sx={compact ? getPositionDrawingCompactSearchInputSx(theme) : getPositionDrawingSearchInputSx(theme)}
          component="label"
        >
          <MdSearch size={18} color={theme.palette.text.secondary} aria-hidden />
          <TextField
            type="search"
            placeholder={t('searchByDrawingNumber')}
            value={filters.searchInput}
            onChange={e => onSearchChange(e.target.value)}
            aria-label={t('searchByDrawingNumber')}
            sx={{
              flex: 1,
              minWidth: 0,
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              '& .MuiInputBase-input': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                height: '0px'
              },
            }}
            fullWidth
          />
        </Box>

        {!compact && <Divider orientation="vertical" flexItem sx={getPositionDrawingFilterDividerSx(theme)} />}

        <Button
          size="small"
          onClick={e => setFilterAnchor(e.currentTarget)}
          sx={{
            ...getPositionDrawingFilterButtonSx(theme),
            ...(compact && { width: '100%', minHeight: 36, borderTop: `1px solid ${theme.palette.divider}` }),
          }}
          startIcon={<TuneIcon sx={{ fontSize: 18, color: getThemeColor(theme, MUIThemeColors.primaryDark) }} />}
        >
          {t('filter', { ns: 'common' })}
        </Button>
      </Box>

      <Menu
        anchorEl={filterAnchor}
        open={Boolean(filterAnchor)}
        onClose={() => setFilterAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 0.5,
            minWidth: 200,
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        {sortedProductCategories.length === 0 ? (
          <MenuItem dense disabled sx={{ fontSize: '13px' }}>
            {t('noResultFound', { ns: 'common' })}
          </MenuItem>
        ) : (
          sortedProductCategories.map(category => {
            const count = selectionCount(category.id)
            return (
              <PositionDrawingFilterMenuItem
                key={category.id}
                dense
                isSelected={count > 0}
                onClick={e => {
                  e.preventDefault()
                  e.stopPropagation()
                  addCategory(category.id)
                }}
              >
                <Typography component="span" variant="body2" sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
                  {getProductCategoryDisplayName(category)}
                </Typography>
                {count > 0 && (
                  <Typography component="span" variant="caption" color="text.secondary">
                    {count}
                  </Typography>
                )}
              </PositionDrawingFilterMenuItem>
            )
          })
        )}
      </Menu>

      <PositionDrawingFilterChipList
        productCategoryIds={filters.productCategoryIds}
        categoryById={categoryById}
        onReorder={onProductCategoriesChange}
        onRemoveAt={removeCategoryAt}
        onClearAll={() => onProductCategoriesChange([])}
      />
    </Card>
  )
}
